import { useTheme, useMediaQuery } from '@mui/material';
import { useAppSelector, useAppDispatch } from '@store/index';
import { selectSidebarMode, selectChatOpen, selectChatWidth, setSidebarMode, SidebarMode } from '@features/ui/uiSlice';
import { LAYOUT } from '../constants/layout';
import { useEffect } from 'react';

export interface ResponsiveLayout {
  sidebarMode: SidebarMode;
  sidebarWidth: number;
  chatOpen: boolean;
  chatWidth: number;
  chatIsOverlay: boolean;
  mainContentMaxWidth: number | undefined;
  isMobile: boolean;
  isUltraWide: boolean;
}

export function useResponsiveLayout(): ResponsiveLayout {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const sidebarMode = useAppSelector(selectSidebarMode);
  const chatOpen = useAppSelector(selectChatOpen);
  const customChatWidth = useAppSelector(selectChatWidth);

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const isLgUp = useMediaQuery(theme.breakpoints.up('lg'));
  const isXlUp = useMediaQuery(theme.breakpoints.up('xl'));
  const isUltraWide = useMediaQuery('(min-width:1920px)');

  // Auto-hide sidebar on mobile
  useEffect(() => {
    if (isMobile && sidebarMode !== 'hidden') {
      dispatch(setSidebarMode('hidden'));
    } else if (!isMobile && sidebarMode === 'hidden') {
      dispatch(setSidebarMode('mini'));
    }
  }, [isMobile, sidebarMode, dispatch]);

  // Determine effective sidebar mode
  const effectiveSidebarMode: SidebarMode = isMobile ? 'hidden' : sidebarMode;

  // Sidebar width for layout calculations
  // 'mini' → 64px (hover expand is overlay, doesn't push content)
  // 'expanded' → 240px (permanent expansion, pushes content)
  // 'hidden' → 0
  let sidebarWidth = 0;
  if (effectiveSidebarMode === 'expanded') sidebarWidth = LAYOUT.SIDEBAR_WIDTH_EXPANDED;
  else if (effectiveSidebarMode === 'mini') sidebarWidth = LAYOUT.SIDEBAR_WIDTH_MINI;

  // Chat panel width: user-set value takes priority, but only on non-overlay
  let chatWidth: number;
  if (customChatWidth && isMdUp) {
    chatWidth = customChatWidth;
  } else if (isXlUp) {
    chatWidth = LAYOUT.CHAT_PANEL_WIDTH.xl;
  } else if (isLgUp) {
    chatWidth = LAYOUT.CHAT_PANEL_WIDTH.lg;
  } else if (isMdUp) {
    chatWidth = LAYOUT.CHAT_PANEL_WIDTH.md;
  } else {
    chatWidth = LAYOUT.CHAT_PANEL_WIDTH.sm;
  }

  // Below md: chat is overlay (doesn't push content)
  const chatIsOverlay = !isMdUp;

  // Ultra-wide: cap content width
  const mainContentMaxWidth = isUltraWide ? LAYOUT.MAIN_CONTENT_MAX_WIDTH : undefined;

  return {
    sidebarMode: effectiveSidebarMode,
    sidebarWidth,
    chatOpen,
    chatWidth: chatOpen ? chatWidth : 0,
    chatIsOverlay,
    mainContentMaxWidth,
    isMobile,
    isUltraWide,
  };
}
